import i18n from "@/i18n";
/**
 * AbsenceManager — Prompt 18B1.
 *
 * Compact Employee Profile card that lists ACTIVE absences and
 * exposes canonical Create / Edit / Cancel controls for
 * authorized managers. Backend endpoints:
 *   • POST   /api/workforce/absences
 *   • GET    /api/workforce/absences
 *   • PATCH  /api/workforce/absences/{absence_id}
 *   • DELETE /api/workforce/absences/{absence_id}
 *
 * Sensitive data boundary (§4): the manager note is presented as
 * an "Operational note (optional)" input and NEVER as a medical /
 * diagnosis / symptom / doctor field.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatYMDDate } from "@/components/workforce/workforceHelpers";
import {
    creditYearLabel, VL_BOOKING_STATE,
} from "@/components/workforce/vuosilomaHelpers";
import AbsenceEditorDialog from "@/components/workforce/AbsenceEditorDialog";
import {
    ABSENCE_TYPE_LABELS, canManageAbsences,
} from "@/components/workforce/absenceShared";

// CALENDAR-ABSENCES-V1 (G7) — labels, roles and copy live in absenceShared.
export {
    ABSENCE_TYPE_LABELS, ABSENCE_TYPES, ABSENCE_AUTHORIZED_ROLES,
    _MSG_TYPE_FACT_ONLY, _MSG_PAID_LEAVE_HELPER, _MSG_OVERLAP,
    _MSG_PERIOD_CLOSED, _MSG_GENERIC, _MSG_CANCEL_TITLE, _MSG_CANCEL_BODY,
    _NOTE_HELPER,
} from "@/components/workforce/absenceShared";

export function annualLeaveRowLabel(r) {
    if (!r.vuosiloma_funding_year) return VL_BOOKING_STATE.ABSENCE_RECORD_UNALLOCATED;
    return `${creditYearLabel(r.vuosiloma_funding_year)} · `
        + (r.vuosiloma_employer_confirmed
            ? "employer-confirmed" : "pending reservation");
}

function _fmtRange(start, end) {
    if (start === end) return formatYMDDate(start);
    return `${formatYMDDate(start)} – ${formatYMDDate(end)}`;
}

export default function AbsenceManager({
    restaurantId, user, employeeId, employmentId, onChanged,
}) {
    const authorized = canManageAbsences(user);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [target, setTarget] = useState(null);
    const busy = !!target;

    const load = useCallback(async () => {
        if (!restaurantId || !employeeId) return;
        setLoading(true);
        try {
            const { data } = await api.get(
                "/workforce/absences",
                { params: { restaurant_id: restaurantId,
                                employee_id: employeeId } });
            setRows(data?.absences || []);
        } catch {
            setRows([]);
        } finally { setLoading(false); }
    }, [restaurantId, employeeId]);

    useEffect(() => { load(); }, [load]);

    const open = (mode, absence) => setTarget({
        key: `${mode}-${absence?.absence_id || "new"}-${Date.now()}`,
        mode, absence, employeeId, employmentId,
    });
    const saved = async () => {
        setTarget(null);
        await load();
        onChanged?.();
    };

    return (
        <section data-testid="employee-profile-absences">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-neutral-700">
                    {i18n.t("wf.absenceManager.absences", "Absences")}
                </h3>
                {authorized && (
                    <Button variant="outline" size="sm"
                        onClick={() => employmentId && open("add")}
                        disabled={busy}
                        data-testid="absence-add-btn">
                        {i18n.t("wf.absenceManager.addAbsence", "Add absence")}
                    </Button>
                )}
            </div>
            {loading ? (
                <p className="text-sm text-neutral-500"
                    data-testid="absence-loading">…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-neutral-500"
                    data-testid="absence-empty">
                    {i18n.t("wf.absenceManager.noActiveAbsences", "No active absences.")}
                </p>
            ) : (
                <ul className="space-y-2" data-testid="absence-list">
                    {rows.map((r) => (
                        <li key={r.absence_id}
                            data-testid={`absence-row-${r.absence_id}`}
                            className="flex items-center justify-between
                                        rounded-md border border-neutral-200
                                        bg-white p-2 text-sm">
                            <div>
                                <div className="font-medium text-neutral-800"
                                    data-testid={`absence-row-type-${r.absence_id}`}>
                                    {ABSENCE_TYPE_LABELS[r.absence_type]
                                        || r.absence_type}
                                </div>
                                <div className="text-xs text-neutral-500"
                                    data-testid={`absence-row-range-${r.absence_id}`}>
                                    {_fmtRange(r.start_date, r.end_date)}
                                </div>
                                {r.absence_type === "ANNUAL_LEAVE" && (
                                    <div className="text-xs text-sky-800"
                                        data-testid={`absence-row-vl-${r.absence_id}`}>
                                        {annualLeaveRowLabel(r)}
                                    </div>
                                )}
                            </div>
                            {authorized && (
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="sm"
                                        onClick={() => open("edit", r)}
                                        disabled={busy}
                                        data-testid={
                                            `absence-edit-${r.absence_id}`}>
                                        {i18n.t("common.edit", "Edit")}
                                    </Button>
                                    <Button variant="ghost" size="sm"
                                        onClick={() => open("cancel", r)}
                                        disabled={busy}
                                        data-testid={
                                            `absence-cancel-${r.absence_id}`}>
                                        {i18n.t("wf.absenceManager.cancelAbsence", "Cancel absence")}
                                    </Button>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {target && (
                <AbsenceEditorDialog key={target.key} target={target}
                    restaurantId={restaurantId}
                    onClose={() => setTarget(null)} onSaved={saved} />
            )}
        </section>
    );
}
